// Website input handling shared by the dashboard and the Worker: what an owner types ("praxis-weber.de")
// becomes an https URL, or null. Pure (only the global URL), so the web bundle can import it via
// `@lobby/shared/website` without pulling in zod.

const BLOCKED_SUFFIXES = ['.localhost', '.local', '.internal', '.lan', '.home.arpa', '.intranet', '.corp', '.private']

/**
 * Returns the URL when it is safe to fetch from the Worker, else null: http(s) only, default
 * ports, a public-looking DNS name (no IP literals, no localhost, no single-label or internal names),
 * no credentials. DNS rebinding is not covered; Workers cannot reach private networks without a binding.
 */
export function safePublicUrl(input: string): URL | null {
  let u: URL
  try {
    u = new URL(input)
  } catch {
    return null
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
  if (u.username || u.password) return null
  if (u.port && u.port !== '80' && u.port !== '443') return null
  const host = u.hostname.toLowerCase().replace(/\.$/, '')
  if (!host || host.includes(':') || host.startsWith('[')) return null // IPv6 literal
  if (/^[\d.]+$/.test(host)) return null // IPv4 literal (also decimal/short forms like 2130706433, 127.1)
  if (/^0x[0-9a-f]+$/i.test(host) || /(^|\.)0x[0-9a-f]+(\.|$)/i.test(host)) return null // hex forms
  if (host === 'localhost' || !host.includes('.')) return null
  if (BLOCKED_SUFFIXES.some((s) => host.endsWith(s))) return null
  if (!/^[a-z0-9.-]+$/.test(host)) return null // punycode only (URL() already converts IDNs)
  return u
}

/**
 * Normalises what an owner typed into an http(s) URL, or null. Bare domains get `https://`;
 * the hostname is lower-cased, the fragment dropped (`praxis-weber.de` → `https://praxis-weber.de/`).
 */
export function normalizeWebsite(input: string): string | null {
  const s = input.trim()
  if (!s || s.length > 500) return null
  const withScheme = /^https?:\/\//i.test(s) ? s : `https://${s}`
  const u = safePublicUrl(withScheme)
  if (!u) return null
  u.hash = ''
  return u.toString()
}

/** Alias used by the dashboard: the same rules the Worker applies, so whatever passes here is accepted there. */
export const normalizeWebsiteInput = normalizeWebsite

// Second-level labels that sit under a country TLD (co.uk, com.au, co.at, or.at, org.nz, ...).
const SECOND_LEVEL = new Set(['co', 'com', 'org', 'net', 'ac', 'gov', 'edu', 'or', 'gv', 'ltd', 'plc', 'me', 'nom', 'gen'])

/** Label words of the registrable name: `www.dr-mueller-zahnarzt.com` → `dr`, `mueller`, `zahnarzt`. */
function registrableLabel(host: string): string {
  const parts = host.toLowerCase().replace(/\.$/, '').replace(/^www\./, '').split('.')
  if (parts.length < 2) return parts[0] ?? ''
  const tld = parts.at(-1)!
  const sld = parts.at(-2)!
  const twoPart = parts.length >= 3 && tld.length === 2 && SECOND_LEVEL.has(sld)
  return parts.at(twoPart ? -3 : -2)!
}

/**
 * A business name guess from the website: the registrable label, split on `-`, `_` and `.`,
 * each word Title Case. `praxis-weber.de` → "Praxis Weber", `summit-roofing.co.uk` → "Summit Roofing".
 * Empty string when the input is not a valid website.
 */
export function businessNameFromWebsite(url: string): string {
  const normalized = normalizeWebsite(url)
  if (!normalized) return ''
  return registrableLabel(new URL(normalized).hostname)
    .split(/[-_.]+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase() + w.slice(1))
    .join(' ')
}
